/**
 * Welle 114/119 – Rechnung / Gutschrift (Seite „Rechnungen", Reiter „Rechnung schreiben").
 *
 * Aussteller und Empfänger je in EINEM Suchfeld (Welle 120) → „Vorbelegen": alles kommt aus DB 2
 * (Laschen der Daten.xlsm) und ist frei änderbar. Nummer <Kürzel>-<JJ>-<NNNN> je Rechnungssteller, fest erst beim
 * Stellen. Positionen von Hand, aus Excel einfügen, in Google bearbeiten (eine Lasche je Rechnung) oder aus der
 * Arbeitszeit übernehmen. „Stellen" trägt die Rechnung ins Rechnungsausgangsbuch ein, legt das PDF mit QR-Code in
 * der Dropbox ab und markiert übernommene Arbeitszeit als abgerechnet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArbeitszeitAuswahl from "./ArbeitszeitAuswahl";
import AnlagenBlock from "./AnlagenBlock";
import MailVersand, { VersandProtokoll } from "./MailVersand";
import { useBausteine } from "./BausteinWahl";
import PositionenTabelle, { summen } from "./PositionenTabelle";
import Suchfeld from "./Suchfeld";
import TextFelder from "./TextFelder";
import { API, zahl, euro, deDatum, json, dropboxDatei } from "./hilfen";
import "../mietverwaltung/Tab5Bescheinigungen.css";
import "./Rechnungen.css";
import Ico from "./Ico";

const FREI = { art: "frei", id: "", label: "— frei eingeben (ohne Datenbank) —", gruppe: "" };
const plusTage = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const tageZwischen = (von, bis) => Math.round((new Date(`${bis}T12:00:00`) - new Date(`${von}T12:00:00`)) / 86400000);

function Feld({ label, children, breit }) {
  return (
    <label className="mb-feld" style={breit ? { gridColumn: "1 / -1" } : undefined}>
      <span className="mb-label">{label}</span>
      {children}
    </label>
  );
}

// Platzhalter wie im PDF (rechnung._platzhalter) — nur zur Anzeige unter dem Textfeld
function platzhalter(text, f, s) {
  const b = f.bank || {};
  const iban = (b.iban || "").trim(), bank = (b.bank || "").trim();
  const werte = {
    rg_nr: f.rg_nr || "", faellig_am: deDatum(f.faellig_am), datum: deDatum(f.datum), brutto: euro(s.brutto),
    netto: euro(s.netto), iban, bic: b.bic || "", bank, kontoinhaber: b.kontoinhaber || "",
    konto: iban ? `IBAN ${iban}${bank ? ` bei der ${bank}` : ""}` : "(Konto fehlt)", objekt: f.objekt || "",
    zahlungsziel: f.zahlungsziel_tage !== undefined && f.zahlungsziel_tage !== "" ? `${zahl(f.zahlungsziel_tage)} Tage` : "",
    leistungszeitraum: [deDatum(f.leistung_von), deDatum(f.leistung_bis)].filter(Boolean).join(" – "),
  };
  return String(text || "").replace(/\{([a-z_]+)\}/g, (m, k) => (k in werte ? werte[k] : m));
}

// Excel-Zeilen (Tab-getrennt) → Positionen: [Pos] Leistung, Menge, Einheit, Einzelpreis, USt %
function ausExcel(text, satzRegel) {
  const raus = [];
  String(text || "").split(/\r?\n/).forEach((zeile) => {
    if (!zeile.trim()) return;
    let z = zeile.split("\t").map((x) => x.trim());
    if (z.length > 1 && /^\d+$/.test(z[0]) && z[1] && zahl(z[1]) === 0 && !/^[\d.,]+$/.test(z[1])) z = z.slice(1);
    let p;
    if (z.length === 1) p = { text: z[0], menge: 1, einheit: "", preis: 0 };
    else if (z.length === 2) p = { text: z[0], menge: 1, einheit: "", preis: zahl(z[1]) };
    else if (z.length === 3) p = { text: z[0], menge: zahl(z[1]) || 1, einheit: "", preis: zahl(z[2]) };
    else p = { text: z[0], menge: zahl(z[1]) || 1, einheit: z[2] || "", preis: zahl(z[3]) };
    let satz = z.length >= 5 && z[4] !== "" ? zahl(z[4].replace("%", "")) : satzRegel;
    if (satz > 0 && satz < 1) satz = Math.round(satz * 10000) / 100;
    if (/^(leistung|bezeichnung|text|pos)/i.test(p.text) && !p.preis) return;     // Kopfzeile mitkopiert
    raus.push({ ...p, satz, quelle: "excel" });
  });
  return raus;
}

export default function RechnungPanel({ mietvertragNr = "", objekt = "", auftrag = null, onBuchGeaendert }) {
  const [ausstellerListe, setAusstellerListe] = useState([]);
  const [typ, setTyp] = useState("Rechnung");
  const [aussteller, setAussteller] = useState("");
  const [empf, setEmpf] = useState({ art: "", id: "", label: "" });
  const [form, setForm] = useState(null);
  const [bausteine, ladeBausteine] = useBausteine();
  const [inDb, setInDb] = useState(null);           // Vorschau „In DB übernehmen"
  const [pruef, setPruef] = useState(null);
  const [ordner, setOrdner] = useState("");
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState(null);
  // Welle 126: Rückfrage beim Stellen auf der Seite (keine Browser-Dialoge); mail = direkt per E-Mail senden
  const [stellFrage, setStellFrage] = useState(null);     // {mail, an, betreff, text, postfach, nr_vorschlag}
  const [protokoll, setProtokoll] = useState([]);
  const [google, setGoogle] = useState(null);
  const [excelOffen, setExcelOffen] = useState(false);
  const [excelText, setExcelText] = useState("");
  const [azOffen, setAzOffen] = useState(false);

  const zeige = (ok, text, dateien = null) => setMeldung({ ok, text, dateien });
  const buchGeaendert = () => onBuchGeaendert && onBuchGeaendert();

  useEffect(() => {
    fetch(`${API}/aussteller`).then(json).then((d) => setAusstellerListe(d.aussteller || [])).catch(() => {});
    fetch(`${API}/google/status`).then(json).then(setGoogle).catch(() => setGoogle({ verfuegbar: false, hinweis: "Google-Status nicht abrufbar." }));
  }, []);

  const vorbelegen = useCallback(async (aid = aussteller, art = empf.art || "frei", eid = empf.id, rgTyp = typ) => {
    if (!aid) { zeige(false, "Bitte einen Aussteller wählen."); return; }
    setBusy(true); setMeldung(null);
    try {
      const q = new URLSearchParams({ aussteller: aid, empfaenger_art: art, empfaenger_id: art === "frei" ? "" : eid, rg_typ: rgTyp });
      const d = await json(await fetch(`${API}/vorbelegung?${q}`));
      setForm(d); setOrdner(""); setPruef(null);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  }, [aussteller, empf, typ]);

  // Welle 120: Stammdaten einer offenen Rechnung (auch alter Entwurf) neu aus der DB — Positionen, Texte, Datum,
  // Nummer und Id bleiben. teil: "empfaenger" | "aussteller" | "beide"
  const anredeErsetzen = (alt, neu) => {
    const a = String(alt || "").split("\n"), n = String(neu || "").split("\n");
    const istAnrede = (z) => /^(sehr geehrte|liebe|hallo|guten tag)/i.test(String(z || "").trim());
    if (istAnrede(a[0]) && istAnrede(n[0])) { a[0] = n[0]; return a.join("\n"); }
    return alt;
  };
  const stammdatenLaden = async (teil, aid, art, eid) => {
    const f0 = form;
    aid = aid ?? f0.aussteller; art = art ?? (f0.empfaenger_art || "frei"); eid = eid ?? (f0.empfaenger_id || "");
    if (!aid) { zeige(false, "Aussteller unbekannt — bitte oben einen Aussteller wählen."); return; }
    setBusy(true); setMeldung(null);
    try {
      const q = new URLSearchParams({ aussteller: aid, empfaenger_art: art, empfaenger_id: art === "frei" ? "" : eid, rg_typ: f0.rg_typ || typ });
      const d = await json(await fetch(`${API}/vorbelegung?${q}`));
      setForm((f) => {
        let n = { ...f };
        if (teil !== "aussteller") {
          n = { ...n, empfaenger_art: d.empfaenger_art, empfaenger_id: d.empfaenger_id, mietvertrag_nr: d.mietvertrag_nr,
            empfaenger: d.empfaenger, objekt: d.objekt, debitor: d.debitor, mwst_ausweis: d.mwst_ausweis, vorlagen: d.vorlagen,
            anrede: d.anrede || f.anrede, text_vor: anredeErsetzen(f.text_vor, d.text_vor) };
        }
        if (teil !== "empfaenger") {
          n = { ...n, aussteller: d.aussteller, kreis: d.kreis, absender: { ...d.absender, vertreten_durch: f.absender?.vertreten_durch || "" },
            bank: d.bank, bank_optionen: d.bank_optionen, unterschrift: d.unterschrift, ...(f.rg_nr_auto ? { rg_nr: d.rg_nr } : {}) };
        }
        return n;
      });
      zeige(true, teil === "aussteller" ? "Aussteller und Konto neu aus der Datenbank geladen — Positionen und Texte sind geblieben."
        : "Empfänger neu aus der Datenbank geladen — Positionen und Texte sind geblieben.");
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  // Welle 120: offene Rechnung im Browser-Tab merken (sessionStorage) — nach einem Neuladen ist sie wieder da,
  // auch wenn sie noch nicht gespeichert war
  const MERKEN = "imho_rechnung_offen";
  const wiederRef = useRef(false);
  useEffect(() => {
    if (wiederRef.current || mietvertragNr || objekt) { wiederRef.current = true; return; }
    wiederRef.current = true;
    try {
      const m = JSON.parse(sessionStorage.getItem(MERKEN) || "null");
      if (m && m.form) {
        setForm(m.form); setAussteller(m.aussteller || ""); setEmpf(m.empf || { art: "", id: "", label: "" });
        setTyp(m.typ || "Rechnung");
      }
    } catch (_) { /* privates Fenster o. Ä. — dann eben nicht */ }
  }, [mietvertragNr, objekt]);
  useEffect(() => {
    if (!wiederRef.current) return;
    const t = setTimeout(() => {
      try { sessionStorage.setItem(MERKEN, JSON.stringify({ form, aussteller, empf, typ })); } catch (_) { /* voll/gesperrt */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form, aussteller, empf, typ]);

  // Aus Mietverwaltung / URL: Vertrag → Empfänger = Mieter, Aussteller = Objekt des Vertrags (direkt vorbelegt)
  const startRef = useRef("");
  useEffect(() => {
    const key = `${mietvertragNr}|${objekt}`;
    if (key === "|" || startRef.current === key) return;
    startRef.current = key;
    if (mietvertragNr) {
      const aid = mietvertragNr.split("-")[0];
      setEmpf({ art: "mietvertrag", id: mietvertragNr, label: mietvertragNr }); setAussteller(aid);
      vorbelegen(aid, "mietvertrag", mietvertragNr, "Rechnung");
    } else if (objekt) {
      setAussteller(objekt);
    }
  }, [mietvertragNr, objekt, vorbelegen]);

  // Aufträge aus dem Ausgangsbuch: öffnen / duplizieren
  useEffect(() => {
    if (!auftrag) return;
    (async () => {
      setBusy(true); setMeldung(null);
      try {
        let d;
        if (auftrag.art === "oeffnen") {
          d = await json(await fetch(`${API}/${auftrag.id}`));
          setForm(d);
        } else if (auftrag.art === "duplizieren") {
          d = await json(await fetch(`${API}/${auftrag.id}/duplizieren`, { method: "POST" }));
          setForm(d);
          zeige(true, `Vorlage ${d.vorlage_von} übernommen — neuer Entwurf, Nummer wird beim Stellen vergeben.`);
        }
        if (d) {
          setAussteller(d.aussteller || "");
          setTyp(d.rg_typ || "Rechnung");
          setEmpf(d.empfaenger_art ? { art: d.empfaenger_art, id: d.empfaenger_id || "",
            label: [d.empfaenger_id, d.empfaenger?.name].filter(Boolean).join(" · ") || "frei" } : { art: "", id: "", label: "" });
        }
        setOrdner(""); setPruef(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) { zeige(false, String(e.message || e)); }
      setBusy(false);
    })();
  }, [auftrag]);

  // Welle 120: Aussteller (im Browser gefiltert) und Empfänger (alle Arten, Backend) — jedes Wort muss passen
  const ausstellerSuche = useCallback((q) => {
    const w = q.toLowerCase().split(/\s+/).filter(Boolean);
    return ausstellerListe.filter((a) => w.every((x) => `${a.id} ${a.label}`.toLowerCase().includes(x)))
      .map((a) => ({ ...a, gruppe: a.art === "objekt" ? "Objekte" : "Firmen & Personen" }));
  }, [ausstellerListe]);
  const empfaengerSuche = useCallback(async (q) => {
    if (!q) return [];
    return (await json(await fetch(`${API}/empfaenger/alle?q=${encodeURIComponent(q)}`))).empfaenger || [];
  }, []);
  const ausstellerLabel = (ausstellerListe.find((a) => a.id === aussteller) || {}).label || aussteller;

  const offeneRechnung = () => !!form && !form.nur_excel && form.status !== "gestellt" && form.status !== "storniert";
  const empfaengerGewaehlt = (t) => {
    setEmpf(t);
    let aid = aussteller;
    if (!aid && t.art === "mietvertrag") { aid = t.id.split("-")[0]; setAussteller(aid); }
    if (!aid && t.art === "objekt") { aid = t.id; setAussteller(aid); }
    if (!aid) { zeige(false, "Bitte noch den Aussteller wählen."); return; }
    if (offeneRechnung()) stammdatenLaden("empfaenger", aid, t.art, t.id);     // Positionen/Texte bleiben
    else vorbelegen(aid, t.art, t.id, typ);
  };
  const ausstellerGewaehlt = (t) => {
    setAussteller(t.id);
    if (offeneRechnung()) stammdatenLaden("aussteller", t.id);
    else if (empf.art) vorbelegen(t.id, empf.art, empf.id, typ);
  };

  // Pflichtangaben + Ablageordner + Dateiname + Nummernvorschlag (verzögert nach jeder Änderung)
  const pruefRef = useRef(0);
  useEffect(() => {
    if (!form || form.nur_excel || form.status === "gestellt" || form.status === "storniert") return;
    const nr = ++pruefRef.current;
    const t = setTimeout(async () => {
      try {
        const d = await json(await fetch(`${API}/pruefen`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ daten: form }) }));
        if (nr !== pruefRef.current) return;
        setPruef(d);
        if (d.rg_nr_vorschlag) setForm((f) => (f && f.rg_nr_auto && f.rg_nr !== d.rg_nr_vorschlag ? { ...f, rg_nr: d.rg_nr_vorschlag } : f));
      } catch (_) { /* Prüfung ist nur Hinweis */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form]);

  const gesperrt = !!form && (form.status === "gestellt" || form.status === "storniert" || form.nur_excel);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIn = (gruppe, k, v) => setForm((f) => ({ ...f, [gruppe]: { ...(f[gruppe] || {}), [k]: v } }));
  const leerePos = (p) => (p.typ || "position") === "position" && !String(p.text || "").trim() && !zahl(p.preis);
  const posDazu = (neu) => setForm((f) => ({ ...f, positionen: [...f.positionen.filter((p) => !leerePos(p)), ...neu] }));

  const s = useMemo(() => summen((form?.positionen || []).filter((p) => !leerePos(p))), [form]);
  const azImFormular = useMemo(() => new Set((form?.positionen || []).flatMap((p) => (p.az || []).map(([t, i]) => `${t}:${i}`))), [form]);

  const vorlageMiete = () => {
    posDazu((form.vorlagen?.miete || []).map((p) => ({ ...p })));
    if (form.vorlagen?.miete_hinweis && !form.steuerhinweis) set("steuerhinweis", form.vorlagen.miete_hinweis);
  };
  const vorlageWallbox = () => {
    const von = deDatum(form.leistung_von), bis = deDatum(form.leistung_bis);
    posDazu([{ text: `Ladestrom Wallbox ${von}–${bis} (Zählerstand alt … / neu …)`, menge: 0, einheit: "kWh", preis: 0, satz: form.vorlagen?.satz_regel ?? 19 }]);
  };
  const excelUebernehmen = () => {
    const neu = ausExcel(excelText, form.vorlagen?.satz_regel ?? 19);
    if (!neu.length) { zeige(false, "Keine Zeilen erkannt — bitte Zeilen aus Excel/Google kopieren (Leistung, Menge, Einheit, Einzelpreis, USt %)."); return; }
    posDazu(neu); setExcelText(""); setExcelOffen(false);
    zeige(true, `${neu.length} Position(en) aus der Zwischenablage übernommen.`);
  };

  const pdfHolen = async (ablegen, daten = form) => {
    const r = await fetch(`${API}/pdf?ablegen=${ablegen ? 1 : 0}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ daten, ordner: ablegen && ordner.trim() ? ordner.trim() : null }) });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
    return r;
  };

  const vorschau = async () => {
    setBusy(true);
    try {
      const r = await pdfHolen(false);
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const entwurfSpeichern = async (daten = form) => {
    const d = await json(await fetch(`${API}/entwurf`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ daten }) }));
    setForm((f) => ({ ...f, id: d.id }));
    buchGeaendert();
    return d.id;
  };

  // Fotos/Anlagen brauchen eine Rechnungs-Id → Entwurf bei Bedarf zuerst speichern
  const sicherstellenId = async () => (form?.id ? form.id : entwurfSpeichern());
  // Welle 121: nach einem Upload (Anlage/Foto) den Entwurf gleich speichern — sonst stand die Datei nur im Formular
  const [speichernNach, setSpeichernNach] = useState(0);
  const nachUpload = () => setSpeichernNach((x) => x + 1);
  useEffect(() => {
    if (speichernNach && form?.id && form.status !== "gestellt" && form.status !== "storniert") entwurfSpeichern(form).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speichernNach]);

  // Welle 120: geänderte Anschrift in die Datenbank (Vorschau → bestätigen)
  const empfaengerGeaendert = !!form && !gesperrt && ["adresse", "objekt", "mietvertrag"].includes(form.empfaenger_art)
    && Array.isArray(form.empfaenger?.zeilen_db)                     // alte Entwürfe: erst „aus DB neu laden"
    && JSON.stringify((form.empfaenger?.zeilen || []).map((z) => String(z).trim()).filter(Boolean))
      !== JSON.stringify((form.empfaenger?.zeilen_db || []).map((z) => String(z).trim()).filter(Boolean));
  const inDbVorschau = async () => {
    setBusy(true);
    try {
      const d = await json(await fetch(`${API}/empfaenger/in-db?dry_run=1`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ art: form.empfaenger_art, id: form.empfaenger_id, zeilen: form.empfaenger?.zeilen || [] }) }));
      setInDb(d);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };
  const inDbSpeichern = async () => {
    setBusy(true);
    try {
      await json(await fetch(`${API}/empfaenger/in-db?dry_run=0`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ art: form.empfaenger_art, id: form.empfaenger_id, zeilen: form.empfaenger?.zeilen || [] }) }));
      setForm((f) => ({ ...f, empfaenger: { ...f.empfaenger, zeilen_db: [...(f.empfaenger?.zeilen || [])] } }));
      zeige(true, `${inDb.aenderungen.length} Feld(er) in der Datenbank gespeichert — Google übernimmt es beim nächsten Abgleich.`);
      setInDb(null);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const entwurf = async () => {
    setBusy(true);
    try {
      await entwurfSpeichern();
      zeige(true, "Als Entwurf gespeichert (im Rechnungsausgangsbuch, Nummer wird erst beim Stellen vergeben).");
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const stellZeilen = () => {
    const warn = pruef?.warnungen || [];
    return [`Eintrag ins Rechnungsausgangsbuch, PDF nach: ${ordner.trim() || pruef?.ablage?.pfad || "(Batch Output)"}`,
      `• ${pruef?.dateiname || ""}`, ...(pruef?.eingang?.dateiname ? [`• ${pruef.eingang.dateiname} (Eingangsrechnung)`] : []),
      ...(azImFormular.size ? [`${azImFormular.size} Arbeitszeit-Eintrag/Einträge werden als abgerechnet markiert.`] : []),
      ...(warn.length ? [`Achtung, noch offen: ${warn.join(" · ")}`] : [])];
  };
  const stellenFragen = async (mail) => {
    setProtokoll([]);
    if (!mail) { setStellFrage({ mail: false }); return; }
    setBusy(true);
    try {
      const v = await json(await fetch(`${API}/mail-vorschlag`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ daten: form }) }));
      setStellFrage({ mail: true, an: v.an || "", betreff: v.betreff || "", text: v.text || "", postfach: v.postfach || {}, nr_vorschlag: form.rg_nr });
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const stellen = async () => {
    const mail = stellFrage?.mail ? stellFrage : null;
    setStellFrage(null);
    setBusy(true);
    try {
      const r = await pdfHolen(true);
      const blob = await r.blob();
      const nr = decodeURIComponent(r.headers.get("X-Rechnung-Nr") || "") || form.rg_nr;
      const pfad = r.headers.get("X-Ablage-Pfad") ? decodeURIComponent(r.headers.get("X-Ablage-Pfad")) : "";
      const eingang = r.headers.get("X-Eingang-Pfad") ? decodeURIComponent(r.headers.get("X-Eingang-Pfad")) : "";
      setForm((f) => ({ ...f, id: Number(r.headers.get("X-Rechnung-Id")) || f.id, status: "gestellt", rg_nr: nr, rg_nr_auto: false,
        pdf_pfad: pfad || f.pdf_pfad }));
      if (pfad) {
        // Welle 121: kein automatischer Download mehr — die Datei liegt im Batch Output (von dort verschieben)
        zeige(true, `${form.rg_typ} ${nr} gestellt und in ${pfad.split("/").slice(-2, -1)[0] || "die Dropbox"} abgelegt`
          + (eingang ? " — dazu die Eingangsrechnung für den Empfänger:" : ":"), [pfad, eingang].filter(Boolean));
      } else {
        const a = document.createElement("a");            // Dropbox ging nicht: dann wenigstens herunterladen
        a.href = URL.createObjectURL(blob);
        a.download = pruef?.dateiname || `${nr}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        zeige(false, `${form.rg_typ} ${nr} gestellt — Ablage in der Dropbox hat nicht geklappt, PDF wurde heruntergeladen.`);
      }
      const rid = Number(r.headers.get("X-Rechnung-Id"));
      if (mail && pfad && rid) {
        // die beim Stellen fest vergebene Nummer kann vom Vorschlag abweichen → in Betreff/Text nachziehen
        const nrFix = (t) => (mail.nr_vorschlag && mail.nr_vorschlag !== nr ? t.split(mail.nr_vorschlag).join(nr) : t);
        try {
          const d = await json(await fetch(`${API}/${rid}/mail`, { method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ an: mail.an, betreff: nrFix(mail.betreff), text: nrFix(mail.text) }) }));
          setForm((f) => ({ ...f, versand: d.versand, pdf_pfad: (d.protokoll.find((p) => p.pfad && /richtigen Ordner/.test(p.text)) || {}).pfad || f.pdf_pfad }));
          setProtokoll(d.protokoll || []);
          zeige(true, `${form.rg_typ} ${nr} gestellt und per E-Mail an ${mail.an} gesendet — das wurde gemacht:`);
        } catch (e) {
          zeige(false, `${form.rg_typ} ${nr} ist gestellt und liegt im Batch Output — die E-Mail ging NICHT raus: ${e.message || e}. `
            + "Unten mit „Per E-Mail senden …“ noch einmal versuchen oder die Datei selbst weiterverarbeiten.", [pfad, eingang].filter(Boolean));
        }
      }
      buchGeaendert();
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
    setTimeout(() => document.getElementById("rg-meldung")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };

  const duplizieren = async () => {
    if (!form?.id) return;
    setBusy(true);
    try {
      const d = await json(await fetch(`${API}/${form.id}/duplizieren`, { method: "POST" }));
      setForm(d); setOrdner(""); setPruef(null);
      zeige(true, `Vorlage ${d.vorlage_von} übernommen — neuer Entwurf, Nummer wird beim Stellen vergeben.`);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  // Google: Fenster VOR dem await öffnen (Popup-Blocker), Entwurf vorher speichern
  const inGoogle = async () => {
    const fenster = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const rid = gesperrt ? form.id : await entwurfSpeichern();
      const d = await json(await fetch(`${API}/${rid}/google-oeffnen`, { method: "POST" }));
      if (fenster) { fenster.opener = null; fenster.location = d.url; } else window.open(d.url, "_blank", "noopener");
      setForm((f) => ({ ...f, google_gid: true }));
      zeige(true, d.neu ? `Lasche „${d.lasche}“ angelegt — Positionen dort bearbeiten, danach „Aus Google einlesen“.`
        : `Lasche „${d.lasche}“ geöffnet (vorhandene Änderungen in Google bleiben).`);
    } catch (e) { if (fenster) fenster.close(); zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const ausGoogle = async () => {
    setBusy(true);
    try {
      const rid = await entwurfSpeichern();
      const v = await json(await fetch(`${API}/${rid}/google-einlesen?dry_run=1`, { method: "POST" }));
      setBusy(false);
      if (!window.confirm(`Aus Lasche „${v.lasche}“: ${v.neu} neu, ${v.geaendert} geändert, ${v.entfallen} entfallen — `
        + `${v.positionen.length} Position(en), ${euro(v.summen.brutto)} brutto.\n\nPositionen der Rechnung ersetzen?`)) return;
      setBusy(true);
      const d = await json(await fetch(`${API}/${rid}/google-einlesen?dry_run=0`, { method: "POST" }));
      setForm((f) => ({ ...f, positionen: d.positionen }));
      zeige(true, `${d.positionen.length} Position(en) aus Google übernommen.`);
      buchGeaendert();
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const ziel = pruef?.ablage;
  const googleAus = !google?.verfuegbar;

  return (
    <div className="rg-wrap">
      <div className="mb-sektion">
        <div className="mb-sektion-titel">Neue Rechnung / Gutschrift</div>
        <div className="rg-start rg-start-neu">
          <Feld label="Art">
            <select className="mb-input" value={typ} onChange={(e) => setTyp(e.target.value)}>
              <option>Rechnung</option><option>Gutschrift</option>
            </select>
          </Feld>
          <Feld label="Aussteller (von) — Objekt, Firma oder Person suchen">
            <Suchfeld wert={ausstellerLabel} placeholder="z. B. 006, IHY, Holthey …" laden={ausstellerSuche}
              onWahl={ausstellerGewaehlt} />
          </Feld>
          <Feld label="Empfänger (an) — Mieter, Vertrag, Objekt, Firma, Kreditor … (Teile reichen)">
            <Suchfeld wert={empf.label} placeholder="z. B. Butenko, 005-0603, 0101, TCH Moisburg, 10000 …" laden={empfaengerSuche}
              mitLeer={FREI} onWahl={empfaengerGewaehlt} />
          </Feld>
          <div className="rg-start-knopf">
            <button className="mb-btn primary" disabled={busy || !aussteller || !empf.art} onClick={() => vorbelegen()}>
              Vorbelegen
            </button>
          </div>
        </div>
        <p className="rg-klein">Alles wird aus DB 2 vorbelegt (Objekte, Adressen, Banken, Mietverträge, Mieten) und ist danach frei änderbar.
          Nummer: <b>Kürzel-JJ-NNNN</b> — ein Kreis je Rechnungssteller (Objekte zählen im Kreis ihres Eigentümers), fest vergeben erst beim Stellen.
          Eine alte Rechnung als Vorlage: im Reiter „Ausgangsbuch“ auf „duplizieren“.</p>
      </div>

      {meldung && (
        <div id="rg-meldung" className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}
          {(meldung.dateien || []).map((p) => <DateiZeile key={p} pfad={p} zeige={zeige} />)}
          <VersandProtokoll protokoll={protokoll} />
        </div>
      )}

      {form && (
        <>
          {gesperrt && (
            <div className="mb-hinweis">
              {form.nur_excel ? "Diese Zeile stammt aus der Excel (ohne Formular) — nur ansehen."
                : `${form.rg_typ === "Storno" ? "Stornorechnung" : form.rg_typ} ${form.rg_nr} ist ${form.status === "storniert" ? "storniert" : "gestellt"} — nicht mehr änderbar.`}
              {form.rg_typ === "Storno" && form.storno_zu_nr ? ` Storno zu Rechnung ${form.storno_zu_nr}.` : ""}
              {form.storno_nr ? ` Storniert durch ${form.storno_nr}.` : ""}
              {form.pdf_pfad && !(meldung?.dateien || []).includes(form.pdf_pfad) && <DateiZeile pfad={form.pdf_pfad} zeige={zeige} titel="Abgelegt:" />}
              {" "}{!form.nur_excel && <button className="mb-link" onClick={duplizieren}><Ico n="kopie" /> duplizieren (als Vorlage)</button>}
              {" "}<button className="mb-link" onClick={() => setForm(null)}>schließen</button>
            </div>
          )}
          {/* Welle 126: gestellte Rechnung per E-Mail (rechnung@) — oder wie bisher selbst herunterladen */}
          {gesperrt && !form.nur_excel && form.id && <MailVersand key={`${form.id}-${form.versand?.am || ""}`} rid={form.id} onGesendet={() => buchGeaendert()} />}
          {form.vorlage_von && !form.id && (
            <div className="mb-hinweis">Neuer Entwurf nach Vorlage <b>{form.vorlage_von}</b> — Empfänger, Texte und Positionen prüfen.</div>
          )}
          <fieldset className="rg-fieldset" disabled={gesperrt}>
            <div className="rg-zwei">
              <div className="mb-sektion">
                <div className="mb-sektion-titel">{form.rg_typ} · Kopf</div>
                <div className="mb-felder">
                  <Feld label={form.rg_nr_auto ? `${form.rg_typ}snummer (vorläufig — fest beim Stellen)` : `${form.rg_typ}snummer`}>
                    <div className="rg-suche">
                      <input className={`mb-input${form.rg_nr_auto ? " rg-vorlaeufig" : ""}`} value={form.rg_nr || ""}
                        onChange={(e) => setForm((f) => ({ ...f, rg_nr: e.target.value, rg_nr_auto: false }))} />
                      {!form.rg_nr_auto && !gesperrt && form.kreis && (
                        <button type="button" className="mb-btn" title="Nummer wieder automatisch vergeben"
                          onClick={() => setForm((f) => ({ ...f, rg_nr_auto: true }))}><Ico n="aktualisieren" /> auto</button>
                      )}
                    </div>
                  </Feld>
                  <Feld label="Datum"><input type="date" className="mb-input" value={form.datum || ""} onChange={(e) => {
                    const v = e.target.value; const n = parseInt(form.zahlungsziel_tage, 10);
                    setForm((f) => ({ ...f, datum: v, ...(v && Number.isFinite(n) ? { faellig_am: plusTage(v, n) } : {}) }));
                  }} /></Feld>
                  <Feld label="Leistung von"><input type="date" className="mb-input" value={form.leistung_von || ""} onChange={(e) => set("leistung_von", e.target.value)} /></Feld>
                  <Feld label="Leistung bis"><input type="date" className="mb-input" value={form.leistung_bis || ""} onChange={(e) => set("leistung_bis", e.target.value)} /></Feld>
                  {form.rg_typ !== "Gutschrift" && (
                    <>
                      <Feld label="Zahlungsziel (Tage)">
                        <input className="mb-input rg-zahl" value={form.zahlungsziel_tage ?? ""} onChange={(e) => {
                          const v = e.target.value; const n = parseInt(v, 10);
                          setForm((f) => ({ ...f, zahlungsziel_tage: v, ...(Number.isFinite(n) && f.datum ? { faellig_am: plusTage(f.datum, n) } : {}) }));
                        }} />
                      </Feld>
                      <Feld label="Zahlbar bis"><input type="date" className="mb-input" value={form.faellig_am || ""} onChange={(e) => {
                        const v = e.target.value;
                        setForm((f) => ({ ...f, faellig_am: v, ...(v && f.datum ? { zahlungsziel_tage: tageZwischen(f.datum, v) } : {}) }));
                      }} /></Feld>
                    </>
                  )}
                  <Feld label="Objekt / Einheit"><input className="mb-input" value={form.objekt || ""} onChange={(e) => set("objekt", e.target.value)} /></Feld>
                  <Feld label="Kunden-Nr. (Debitor)"><input className="mb-input" value={form.debitor || ""} onChange={(e) => set("debitor", e.target.value)} /></Feld>
                  <Feld label="Ansprechpartner (leer = nicht drucken)"><input className="mb-input" value={form.ansprechpartner || ""} onChange={(e) => set("ansprechpartner", e.target.value)} /></Feld>
                </div>
              </div>
              <div className="mb-sektion">
                <div className="mb-sektion-titel">Empfänger <span className="mb-badge db">DB</span>
                  {!gesperrt && ["adresse", "objekt", "mietvertrag"].includes(form.empfaenger_art) && (
                    <button type="button" className="mb-link rg-neu-laden" disabled={busy} onClick={() => stammdatenLaden("empfaenger")}
                      title="Anschrift, Anrede, Objekt und Kunden-Nr. neu aus der Datenbank — Positionen und Texte bleiben"><Ico n="aktualisieren" gr={13} /> aus DB neu laden</button>
                  )}
                </div>
                <div className="mb-felder">
                  <Feld label="Anschrift (eine Zeile je Zeile)" breit>
                    <textarea className="mb-input rg-area" rows={5} value={(form.empfaenger?.zeilen || []).join("\n")}
                      onChange={(e) => setIn("empfaenger", "zeilen", e.target.value.split("\n"))} />
                  </Feld>
                  {empfaengerGeaendert && (
                    <div className="rg-indb" style={{ gridColumn: "1 / -1" }}>
                      <button type="button" className="mb-btn" disabled={busy} onClick={inDbVorschau}
                        title="Die geänderte Anschrift in die Datenbank (DB 2) schreiben — Google übernimmt es beim nächsten Abgleich"><Ico n="speichern" /> In DB übernehmen</button>
                      <span className="rg-klein" style={{ margin: 0 }}>Anschrift weicht von der Datenbank ab.</span>
                    </div>
                  )}
                  {inDb && (
                    <div className="rg-indb-vorschau" style={{ gridColumn: "1 / -1" }}>
                      {inDb.aenderungen.length ? (
                        <table className="rg-tabelle">
                          <thead><tr><th>Tabelle</th><th>Schlüssel</th><th>Feld</th><th>bisher</th><th>neu</th></tr></thead>
                          <tbody>{inDb.aenderungen.map((a, i) => (
                            <tr key={i}><td>{a.tabelle}</td><td>{a.schluessel}</td><td>{a.feld}</td><td>{a.alt || "—"}</td><td><b>{a.neu || "—"}</b></td></tr>
                          ))}</tbody>
                        </table>
                      ) : <p className="rg-klein" style={{ marginTop: 0 }}>Keine Änderung für die Datenbank.</p>}
                      {inDb.hinweise.map((h) => <p key={h} className="rg-warn" style={{ margin: "4px 0" }}>{h}</p>)}
                      <div className="rg-knoepfe">
                        {inDb.aenderungen.length > 0 && <button type="button" className="mb-btn primary" disabled={busy} onClick={inDbSpeichern}>Speichern</button>}
                        <button type="button" className="mb-btn" onClick={() => setInDb(null)}>abbrechen</button>
                      </div>
                    </div>
                  )}
                  <Feld label="Name (Rechnungsausgangsbuch, Dateiname)" breit>
                    <input className="mb-input" value={form.empfaenger?.name || ""} onChange={(e) => setIn("empfaenger", "name", e.target.value)} />
                  </Feld>
                  <Feld label="E-Mail (für den Versand per Mail)" breit>
                    <input className="mb-input" value={form.empfaenger_mail || ""} placeholder="aus den Stammdaten — änderbar"
                      onChange={(e) => set("empfaenger_mail", e.target.value.trim())} />
                  </Feld>
                </div>
              </div>
            </div>

            <div className="rg-zwei">
              <div className="mb-sektion">
                <div className="mb-sektion-titel">Aussteller <span className="mb-badge db">DB</span>{form.kreis ? <span className="rg-kreis" title={form.kreis === "OBJ" ? "Objekt-Nr. – Jahr – Buchstabe des Objekts + laufende Nummer über alle Objekte" : ""}>
                  Nummernkreis {form.kreis === "OBJ" ? `Objekte${/^\d{3}-\d{2}-[A-Z]/.test(form.rg_nr || "") ? ` (${form.aussteller} = ${form.rg_nr.split("-")[2][0]})` : ""}` : form.kreis}</span> : null}
                  {!gesperrt && form.aussteller && (
                    <button type="button" className="mb-link rg-neu-laden" disabled={busy} onClick={() => stammdatenLaden("aussteller")}
                      title="Absender und Bankverbindung neu aus der Datenbank — Positionen und Texte bleiben"><Ico n="aktualisieren" gr={13} /> aus DB neu laden</button>
                  )}
                </div>
                <div className="mb-felder">
                  {[["name", "Name"], ["strasse", "Straße"], ["plz_ort", "PLZ Ort"], ["telefon", "Telefon"], ["mail", "E-Mail"],
                    ["steuer_nr", "Steuer-Nr."], ["ust_id", "USt-IdNr."], ["vertreten_durch", "vertreten durch (leer = nicht drucken)"],
                    ["geschaeftsfuehrer", "Geschäftsführer (Fußzeile, leer = nicht drucken)"],
                    ["handelsregister", "Handelsregister, z. B. Amtsgericht Tostedt HRB 1234"]].map(([k, l]) => (
                    <Feld key={k} label={l}><input className="mb-input" value={form.absender?.[k] || ""} onChange={(e) => setIn("absender", k, e.target.value)} /></Feld>
                  ))}
                </div>
              </div>
              <div className="mb-sektion">
                <div className="mb-sektion-titel">Bankverbindung <span className="mb-badge db">DB</span></div>
                <div className="mb-felder">
                  {(form.bank_optionen || []).length > 0 && (
                    <Feld label="Konto aus „Banken“" breit>
                      <select className="mb-input" value={form.bank?.suchbegriff || ""}
                        onChange={(e) => { const b = form.bank_optionen.find((x) => x.suchbegriff === e.target.value); if (b) set("bank", { ...b }); }}>
                        {!form.bank_optionen.some((x) => x.suchbegriff === form.bank?.suchbegriff) && <option value={form.bank?.suchbegriff || ""}>{form.bank?.iban ? `${form.bank.suchbegriff || "eigenes"} · ${form.bank.iban}` : "— wählen —"}</option>}
                        {form.bank_optionen.map((b) => <option key={b.suchbegriff + b.iban} value={b.suchbegriff}>{b.label} · {b.iban}</option>)}
                      </select>
                    </Feld>
                  )}
                  {[["kontoinhaber", "Kontoinhaber"], ["bank", "Bank"], ["iban", "IBAN"], ["bic", "BIC"]].map(([k, l]) => (
                    <Feld key={k} label={l}><input className="mb-input" value={form.bank?.[k] || ""} onChange={(e) => setIn("bank", k, e.target.value)} /></Feld>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-sektion">
              <div className="mb-sektion-titel">Text</div>
              <div className="mb-felder">
                <Feld label="Betreff" breit><input className="mb-input" value={form.betreff || ""} onChange={(e) => set("betreff", e.target.value)} /></Feld>
                <Feld label="Anrede (aus der Datenbank, änderbar)" breit>
                  <input className="mb-input" value={form.anrede ?? ""} onChange={(e) => set("anrede", e.target.value)} />
                </Feld>
                <div className="mb-feld" style={{ gridColumn: "1 / -1" }}>
                  <span className="mb-label">Text vor den Positionen — jedes Feld ein Absatz; „Baustein“ legt ein neues Feld an</span>
                  <TextFelder felder={form.texte_vor || []} setFelder={(fn) => setForm((f) => ({ ...f, texte_vor: fn(f.texte_vor || []) }))}
                    bausteine={bausteine} ladeBausteine={ladeBausteine} gesperrt={gesperrt}
                    standard={form.rg_typ === "Gutschrift" ? "wir schreiben Ihnen folgende Beträge gut:" : "für die folgenden Leistungen berechnen wir Ihnen:"} />
                </div>
              </div>
            </div>

            <div className="mb-sektion">
              <div className="mb-sektion-titel">Positionen</div>
              <PositionenTabelle form={form} setForm={setForm} gesperrt={gesperrt} bausteine={bausteine} ladeBausteine={ladeBausteine}
                sicherstellenId={sicherstellenId} zeige={zeige} nachUpload={nachUpload} />
              <div className="rg-knoepfe">
                <button type="button" className="mb-btn" onClick={() => setAzOffen(true)} title="Stunden aus der Arbeitszeit (Arbeitsnachweis) als Positionen übernehmen"><Ico n="uhr" /> Arbeitszeit übernehmen</button>
                <button type="button" className="mb-btn" onClick={() => setExcelOffen((x) => !x)} title="Zeilen aus Excel/Google kopieren und hier einfügen"><Ico n="klemmbrett" /> Aus Excel einfügen</button>
                {(form.vorlagen?.miete || []).length > 0 && (
                  <button type="button" className="mb-btn" onClick={vorlageMiete} title="Nettokaltmiete + Vorauszahlungen aus der Lasche „Mieten“">
                    Miete wie Dauermietrechnung</button>
                )}
                <button type="button" className="mb-btn" onClick={vorlageWallbox}>Wallbox / Strom</button>
                {!form.nur_excel && (
                  <>
                    <button type="button" className="mb-btn rg-google" disabled={busy || googleAus} onClick={inGoogle}
                      title={google?.hinweis || "Eine Google-Tabelle, je Rechnung eine eigene Lasche — wird beim Öffnen nie überschrieben"}><Ico n="globus" /> In Google bearbeiten</button>
                    <button type="button" className="mb-btn rg-google" disabled={busy || googleAus || gesperrt || !form.google_gid} onClick={ausGoogle}
                      title={form.google_gid ? "Positionen aus der Google-Lasche übernehmen (mit Vorschau)" : "Zuerst „In Google bearbeiten“"}><Ico n="aktualisieren" /> Aus Google einlesen</button>
                  </>
                )}
                <div className="rg-summen">
                  <div><span>Summe netto</span><b>{euro(s.netto)}</b></div>
                  {s.saetze.filter((x) => x.satz).map((x) => (
                    <div key={x.satz}><span>{x.satz} % USt auf {euro(x.netto)}</span><b>{euro(x.ust)}</b></div>
                  ))}
                  <div className="rg-gesamt"><span>{form.rg_typ === "Gutschrift" ? "Gutschriftsbetrag" : "Rechnungsbetrag"}</span><b>{euro(s.brutto)}</b></div>
                </div>
              </div>
              {excelOffen && (
                <div className="rg-excel">
                  <textarea className="mb-input rg-area" rows={5} value={excelText} autoFocus
                    placeholder={"Zeilen aus Excel/Google hier einfügen (Strg/Cmd+V) — Spalten: Leistung ⇥ Menge ⇥ Einheit ⇥ Einzelpreis ⇥ USt %"}
                    onChange={(e) => setExcelText(e.target.value)} />
                  <div className="rg-knoepfe">
                    <button type="button" className="mb-btn primary" onClick={excelUebernehmen}>Zeilen übernehmen</button>
                    <button type="button" className="mb-btn" onClick={() => { setExcelOffen(false); setExcelText(""); }}>abbrechen</button>
                  </div>
                </div>
              )}
            </div>

            {!form.nur_excel && <AnlagenBlock form={form} setForm={setForm} gesperrt={gesperrt} sicherstellenId={sicherstellenId} zeige={zeige}
              nachUpload={nachUpload} />}

            <div className="mb-sektion">
              <div className="mb-sektion-titel">Schluss</div>
              <div className="mb-felder">
                <Feld label="Steuerhinweis (bei 0 % Pflicht)" breit>
                  <div className="rg-suche">
                    <input className="mb-input" value={form.steuerhinweis || ""} onChange={(e) => set("steuerhinweis", e.target.value)} />
                    <button type="button" className="mb-btn" onClick={() => set("steuerhinweis", "Umsatzsteuerfrei gemäß § 4 Nr. 12a UStG (Vermietung und Verpachtung von Grundstücken).")}>§ 4 Nr. 12a</button>
                  </div>
                </Feld>
                <div className="mb-feld" style={{ gridColumn: "1 / -1" }}>
                  <span className="mb-label">Text nach den Positionen — Platzhalter {"{brutto}, {faellig_am}, {zahlungsziel}, {rg_nr}, {konto}, {iban}, {bank}, {objekt}"}</span>
                  <TextFelder felder={form.texte_nach || []} setFelder={(fn) => setForm((f) => ({ ...f, texte_nach: fn(f.texte_nach || []) }))}
                    bausteine={bausteine} ladeBausteine={ladeBausteine} gesperrt={gesperrt} />
                  <span className="rg-klein">So steht es auf der Rechnung: {platzhalter((form.texte_nach || []).filter((t) => String(t).trim()).join("\n\n"), form, s)}</span>
                </div>
                <Feld label="Gruß"><input className="mb-input" value={form.gruss || ""} onChange={(e) => set("gruss", e.target.value)} /></Feld>
                <Feld label="Unterschrift"><input className="mb-input" value={form.unterschrift || ""} onChange={(e) => set("unterschrift", e.target.value)} /></Feld>
              </div>
            </div>
          </fieldset>

          {!gesperrt && (
            <div className="mb-sektion">
              <div className="mb-sektion-titel">Prüfen & ablegen</div>
              {pruef?.warnungen?.length ? (
                <ul className="rg-warn">{pruef.warnungen.map((w) => <li key={w}>{w}</li>)}</ul>
              ) : pruef ? <p className="rg-ok">Pflichtangaben vollständig.</p> : <p className="rg-klein">prüft …</p>}
              {ziel && (
                <div className="rg-ablage">
                  <div><b>Ablage</b> im <b>Batch Output</b> — von dort wie jede umbenannte Datei weiter verschieben:{" "}
                    <code>{ziel.pfad}</code></div>
                  {ziel.hinweis && <div className="rg-klein">{ziel.hinweis}</div>}
                  <div className="rg-dateinamen">
                    <div>Ausgangsrechnung: <code>{pruef.dateiname}</code></div>
                    {pruef.eingang?.dateiname
                      ? <div>Eingangsrechnung: <code>{pruef.eingang.dateiname}</code></div>
                      : <div className="rg-klein">Keine Eingangsrechnung (Empfänger ist keine eigene Firma/kein Objekt).</div>}
                  </div>
                  <div className="mb-felder">
                    <Feld label="Text im Dateinamen (hinten, z. B. Strom 09-2026 — leer = ohne)">
                      <input className="mb-input" value={form.datei_text || ""} maxLength={60}
                        placeholder="z. B. Auslagen Strom, Kleinkläranlage …"
                        onChange={(e) => set("datei_text", e.target.value)} />
                    </Feld>
                    <Feld label="Eingangsrechnung für (Kürzel der eigenen Firma bzw. Objekt-Nr.)">
                      <div className="rg-suche">
                        <input className="mb-input" value={form.eingang_kuerzel ?? pruef.eingang?.vorschlag ?? ""}
                          placeholder="leer = keine Eingangsrechnung" maxLength={12}
                          onChange={(e) => set("eingang_kuerzel", e.target.value.toUpperCase())} />
                        {form.eingang_kuerzel !== undefined && form.eingang_kuerzel !== null && (
                          <button type="button" className="mb-link" onClick={() => set("eingang_kuerzel", null)}
                            title={`automatisch erkennen${pruef.eingang?.vorschlag ? ` (${pruef.eingang.vorschlag})` : ""}`}>automatisch</button>
                        )}
                      </div>
                    </Feld>
                    <Feld label="anderer Ordner (leer = Batch Output)">
                      <input className="mb-input" value={ordner} placeholder="/Holthey Immobilien/…" onChange={(e) => setOrdner(e.target.value)} />
                    </Feld>
                  </div>
                </div>
              )}
              <div className="mb-buttons">
                <button className="mb-btn" disabled={busy} onClick={vorschau}>Vorschau (PDF)</button>
                <button className="mb-btn" disabled={busy} onClick={entwurf}>Als Entwurf speichern</button>
                <button className="mb-btn primary" disabled={busy} onClick={() => stellenFragen(false)}>{busy ? "arbeitet …" : `${form.rg_typ} stellen & in Output ablegen`}</button>
                <button className="mb-btn primary" disabled={busy} onClick={() => stellenFragen(true)}
                  title="Stellen, sofort per E-Mail senden (rechnung@immo.holthey.de), dann im richtigen Ordner ablegen + Kopie für DATEV">
                  <Ico n="verbinden" /> {form.rg_typ} stellen & per E-Mail senden …</button>
              </div>
              {stellFrage && (
                <div className="rg-frage" style={{ marginTop: 10 }}>
                  <b>{form.rg_typ} {form.rg_nr}{form.rg_nr_auto ? " (Nummer wird jetzt fest vergeben)" : ""} über {euro(s.brutto)} jetzt stellen
                    {stellFrage.mail ? " und per E-Mail senden?" : "?"}</b>
                  {stellZeilen().map((z) => <div key={z} className="rg-klein" style={{ margin: 0 }}>{z}</div>)}
                  {stellFrage.mail && (
                    <>
                      {!stellFrage.postfach?.verfuegbar && <div className="mb-meldung fehler">{stellFrage.postfach?.hinweis}</div>}
                      <div className="rg-klein" style={{ margin: 0 }}>Nach dem Senden: aus dem Batch Output in den richtigen Ordner (Lasche „Pfade“) + Kopie in „#Datev hochladen“.
                        Absender {stellFrage.postfach?.absender || "rechnung@immo.holthey.de"}.</div>
                      <div className="mb-felder">
                        <Feld label="An" breit><input className="mb-input" value={stellFrage.an} placeholder="E-Mail des Empfängers"
                          onChange={(e) => setStellFrage({ ...stellFrage, an: e.target.value.trim() })} /></Feld>
                        <Feld label="Betreff" breit><input className="mb-input" value={stellFrage.betreff}
                          onChange={(e) => setStellFrage({ ...stellFrage, betreff: e.target.value })} /></Feld>
                        <Feld label="Text (das PDF hängt an)" breit><textarea className="mb-input rg-area" rows={7} style={{ height: "auto", minHeight: 150 }}
                          value={stellFrage.text} onChange={(e) => setStellFrage({ ...stellFrage, text: e.target.value })} /></Feld>
                      </div>
                    </>
                  )}
                  <div className="rg-knoepfe">
                    <button type="button" className="mb-btn primary" onClick={stellen}
                      disabled={busy || (stellFrage.mail && (!stellFrage.postfach?.verfuegbar || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(stellFrage.an)))}>
                      {stellFrage.mail ? "Ja, stellen & senden" : "Ja, stellen"}</button>
                    <button type="button" className="mb-btn" onClick={() => setStellFrage(null)}>Abbrechen</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {azOffen && form && (
        <ArbeitszeitAuswahl form={form} schonDrin={azImFormular} onClose={() => setAzOffen(false)}
          onUebernehmen={(neu) => { posDazu(neu); setAzOffen(false); zeige(true, `${neu.length} Position(en) aus der Arbeitszeit übernommen.`); }} />
      )}
    </div>
  );
}


// Welle 121: abgelegte Datei mit „ansehen“ / „herunterladen“ (statt automatischem Download beim Stellen)
function DateiZeile({ pfad, zeige, titel }) {
  const tun = (laden) => dropboxDatei(pfad, laden).catch((e) => zeige(false, String(e.message || e)));
  return (
    <div className="rg-datei-zeile">
      {titel && <span>{titel}</span>}
      <Ico n="dokument" /> <code title={pfad}>{pfad.split("/").pop()}</code>
      <button type="button" className="mb-link" onClick={() => tun(false)}>ansehen</button>
      <button type="button" className="mb-link" onClick={() => tun(true)}>herunterladen</button>
    </div>
  );
}
